import CircularProgress from '@material-ui/core/CircularProgress'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import Alert from '@material-ui/lab/Alert'
import axios from 'axios'
import { Form, Formik } from 'formik'
import React, { useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { withTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import i18next from '../i18n/index'

import InfoBox from '../components/InfoBox'
import TextField from '../components/TextField'
import { useAuth } from '../context/auth'
import { LoginInitialValues } from '../helpers/InitialValues'
import { noControlsFilter, unsignedNumberFilter } from '../helpers/RegexConstants'
import { axiosLoginTimeout } from '../helpers/Timeout'
import { SignupSchema } from '../helpers/Validations'
import { useStyles } from './styled'
import { BackButton } from '../components/ReturnButton'

const Login = ({ t }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const [isError, setIsError] = useState(null)
  const [,setIsVerified] = useState(false)
  const [data, setData] = useState({})
  const [dataLang, setDataLang] = useState(t('appl.header.language'))

  const { authTokens, setAuthTokens } = useAuth()
  const language = dataLang

  const recaptchaRef = React.createRef()

  const verify = () => {
    return setIsVerified(true)
  }

  const sendValue = ({ number, code }) => {
    const body = {
      number: number,
      code: code,
    }
    axios({
      method: 'get',
      url: 'api/v1/policy-info',
      timeout: axiosLoginTimeout,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json;charset=utf-8',
        'Accept-Language': language,
      },
      params: body,
    })
      .then((result) => {
        if (result.status === 200) {
          setAuthTokens(true)
          setData(result.data)
          setDataLang(language)
          setIsError(null)
          setLoading(false)
          setIsVerified(true)
        }
      })
      .catch((e) => {
        setLoading(false)
        if (e?.response?.status === 404) {
          setIsError(e?.response)
        } else if (e?.response?.status === 403) {
          setIsError(e?.response)
        } else {
          setIsError(e?.response)
        }
      })
  }

  const statusCheck = () => {
    if (data?.state === 'D') {
      return (
        <Typography className={classes.redText} component={'div'}>
          {t('loginPg.policyInfo.statusNotValid')}
        </Typography>
      )
    } else if (data?.state === 'C') {
      return (
        <Typography className={classes.redText} component={'div'}>
          {t('loginPg.policyInfo.statusCancelled')}
        </Typography>
      )
    } else {
      return (
        <Typography className={classes.greenText} component={'div'}>
          {t('loginPg.policyInfo.statusValid')}
        </Typography>
      )
    }
  }

  const setBackButton = () => {
    return window.location.reload(false)
  }

  return (
    <Formik
      initialValues={LoginInitialValues}
      validationSchema={SignupSchema}
      onSubmit={(values) => {
        setLoading(true)
        sendValue(values)
      }}
    >
      {({ values, errors, handleChange, touched, handleBlur }) => (
        <Form className={classes.root}>
          <Grid item sm={12} xl={12} xs={12} container direction="row" className={classes.gridMainForm}>
            <Grid item sm={9} xl={9} xs={12} container direction="row" className={classes.gridWorkArea}>
              <Grid item sm={12} xs={12} xl={12} container className={classes.gridResize}>
                <Typography className={classes.text} variant="h3">
                  {t('loginPg.title')}
                </Typography>
                <Grid item xs={12} sm={12} xl={12} container className={classes.gridItem} spacing={2}>
                  <Grid item xl={6} xs={6} sm={6} className={classes.fieldSize}>
                    <TextField
                      name="number"
                      regex={unsignedNumberFilter}
                      error={errors.number && touched.number}
                      id="standard-helperText"
                      label={t('loginPg.number.label')}
                      placeholder={t('loginPg.number.hint')}
                      helperText={errors.number && touched.number ? errors.number : null}
                      onChange={handleChange}
                      className={classes.fieldSize}
                      InputProps={{ required: false }}
                      inputProps={{ maxLength: 10 }}
                      onBlur={handleBlur}
                      required
                    />
                  </Grid>
                  <Grid item xl={6} xs={6} sm={6} className={classes.fieldSize}>
                    <TextField
                      name="code"
                      regex={noControlsFilter}
                      error={errors.code && touched.code}
                      id="standard-helperText"
                      label={t('loginPg.code.label')}
                      placeholder={t('loginPg.code.hint')}
                      helperText={errors.code && touched.code ? errors.code : null}
                      onChange={handleChange}
                      className={classes.fieldSize}
                      InputProps={{ required: false }}
                      inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
                      onBlur={handleBlur}
                      required
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={12} xl={12} className={classes.gridButton}>
                  {loading ? (
                    <CircularProgress />
                  ) : (
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey="6LfJklccAAAAAIQSSZ5UoJLTACWfvPEE4ryJ6yex"
                      onChange={() => {
                        verify()
                        sendValue(values)
                      }}
                      name="recaptcha"
                      id="recaptcha"
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={12} xl={12} className={classes.gridButtonBack}>
                  <BackButton onClick={setBackButton}>{t('loginPg.backButton')}</BackButton>
                </Grid>
                <Grid item xs={12} sm={12} xl={12} className={classes.gridButton}>
                  {isError ? (
                    <Alert severity="error" className={classes.error}>
                      {isError.status === 403 && t('loginPg.continueResp.err403')}
                      {isError.status === 404 && t('loginPg.continueResp.err404')}
                    </Alert>
                  ) : (
                    ''
                  )}
                </Grid>
                <Grid item sm={12} xs={12} xl={12} container className={classes.gridItem} spacing={2}>
                  <Grid item sm={12} xs={12} xl={12} className={classes.fieldSize}>
                    {!authTokens ? (
                      <InfoBox />
                    ) : (
                      <Grid item sm={12} xs={12} xl={12} container>
                        <TableContainer component={Paper} className={classes.tablePadding}>
                          <Table size="small" aria-label="a dense table">
                            <TableBody>
                              <TableRow className={classes.tableRowBackground}>
                                <TableCell className={classes.columnWidth}>
                                  <Typography className={classes.tableKeysColor}>
                                    {t('loginPg.policyInfo.customerName')}
                                  </Typography>
                                </TableCell>
                                <TableCell align="left" className={classes.columnWidth}>
                                  <Typography className={classes.tableValuesColor}>{data?.customerName}</Typography>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className={classes.columnWidth}>
                                  <Typography className={classes.tableKeysColor}>
                                    {t('loginPg.policyInfo.productVariantName')}
                                  </Typography>
                                </TableCell>
                                <TableCell align="left" className={classes.columnWidth}>
                                  <Typography className={classes.tableValuesColor}>
                                    {data?.productVariantName}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                              <TableRow className={classes.tableRowBackground}>
                                <TableCell className={classes.columnWidth}>
                                  <Typography className={classes.tableKeysColor}>
                                    {t('loginPg.policyInfo.destinationNames')}
                                  </Typography>
                                </TableCell>
                                <TableCell align="left" className={classes.columnWidth}>
                                  <Typography className={classes.tableValuesColor}>
                                    {data?.destinationNames?.map((el) => el)?.join('\n')}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className={classes.columnWidth}>
                                  <Typography className={classes.tableKeysColor}>
                                    {t('loginPg.policyInfo.validFrom')}
                                  </Typography>
                                </TableCell>
                                <TableCell align="left" className={classes.columnWidth}>
                                  <Typography className={classes.tableValuesColor}>
                                    {dayjs(data?.validFrom).format('DD.MM.YYYY') +
                                      ' - ' +
                                      dayjs(data?.validTo).format('DD.MM.YYYY')}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                              <TableRow className={classes.tableRowBackground}>
                                <TableCell className={classes.columnWidth}>
                                  <Typography className={classes.tableKeysColor}>
                                    {t('loginPg.policyInfo.insuredDays')}
                                  </Typography>
                                </TableCell>
                                <TableCell align="left" className={classes.columnWidth}>
                                  <Typography className={classes.tableValuesColor}>
                                    {data?.insuredDays + ' ' + i18next.t('key', { count: data?.insuredDays })}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className={classes.columnWidth}>
                                  <Typography className={classes.tableKeysColor}>
                                    {t('loginPg.policyInfo.insuredPerson')}
                                  </Typography>
                                </TableCell>
                                <TableCell align="left" className={classes.columnWidth}>
                                  <Typography className={classes.tableValuesColor}>
                                    {data?.insuredPersons?.map((el) => el.name)?.join('\n')}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                              <TableRow className={classes.tableRowBackground}>
                                <TableCell className={classes.columnWidth}>
                                  <Typography className={classes.tableKeysColor}>
                                    {t('loginPg.policyInfo.issuedOn')}
                                  </Typography>
                                </TableCell>
                                <TableCell align="left" className={classes.columnWidth}>
                                  <Typography className={classes.tableValuesColor}>
                                    {dayjs(data?.issuedOn).format('DD.MM.YYYY')}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className={classes.columnWidth}>
                                  <Typography className={classes.tableKeysColor}>
                                    {t('loginPg.policyInfo.number')}
                                  </Typography>
                                </TableCell>
                                <TableCell align="left" className={classes.columnWidth}>
                                  <Typography className={classes.tableValuesColor}>
                                    {data?.productBrand + ' ' + data?.number}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                              <TableRow className={classes.tableRowBackground}>
                                <TableCell className={classes.columnWidth}>
                                  <Typography className={classes.tableKeysColor}>
                                    {t('loginPg.policyInfo.state')}
                                  </Typography>
                                </TableCell>
                                <TableCell align="left" className={classes.columnWidth}>
                                  <Typography className={classes.tableValuesColor}>{statusCheck()}</Typography>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs sm={3} xl={3} />
          </Grid>
        </Form>
      )}
    </Formik>
  )
}

export default withTranslation()(Login)
